import { OutboxTab, OutboxView } from "@/enum/list.enum";

/**
 * The Outbox status tabs (PREC-3850): the five mutually-exclusive backend buckets
 * plus the configurable "All" tab. This module is the single touch-point for the
 * API contract — tab state ↔ URL, the All tab's persisted include-set, and the
 * mapping of a tab selection onto the `outboxTabs` request param.
 */
export enum OutboxStatusTab {
	ALL = "all",
	SCHEDULED = "scheduled",
	IN_PROGRESS = "inProgress",
	ENDED = "ended",
	PAUSED = "paused",
	ERROR = "error",
}

/** Query param the active status tab is persisted under. */
export const STATUS_TAB_PARAM = "outboxTab";

/** Ordered tab strip, as rendered. */
export const OUTBOX_STATUS_TABS: OutboxStatusTab[] = [
	OutboxStatusTab.ALL,
	OutboxStatusTab.SCHEDULED,
	OutboxStatusTab.IN_PROGRESS,
	OutboxStatusTab.ENDED,
	OutboxStatusTab.PAUSED,
	OutboxStatusTab.ERROR,
];

const TAB_TO_API: Record<Exclude<OutboxStatusTab, OutboxStatusTab.ALL>, OutboxTab> = {
	[OutboxStatusTab.SCHEDULED]: OutboxTab.SCHEDULED,
	[OutboxStatusTab.IN_PROGRESS]: OutboxTab.IN_PROGRESS,
	[OutboxStatusTab.ENDED]: OutboxTab.ENDED,
	[OutboxStatusTab.PAUSED]: OutboxTab.PAUSED,
	[OutboxStatusTab.ERROR]: OutboxTab.ERROR,
};

/**
 * Reads the active status tab from a query string. Legacy `outboxView` deep links
 * keep working: SCHEDULED maps to the Scheduled tab, SENT to All (the closest
 * match — "everything that already went out" now lives across several buckets).
 */
export const getStatusTabFromUrl = (search: string): OutboxStatusTab => {
	const params = new URLSearchParams(search);
	const value = params.get(STATUS_TAB_PARAM);

	if (Object.values(OutboxStatusTab).includes(value as OutboxStatusTab)) {
		return value as OutboxStatusTab;
	}

	const legacyView = params.get("outboxView");

	if (legacyView === OutboxView.SCHEDULED) {
		return OutboxStatusTab.SCHEDULED;
	}

	if (legacyView === OutboxView.SENT) {
		return OutboxStatusTab.ALL;
	}

	return OutboxStatusTab.ALL;
};

/** localStorage key of the personal All-tab include-set (deliberately not URL state). */
export const ALL_INCLUDE_STORAGE_KEY = "signalist.outbox.allInclude";

/** Default include-set of the All tab: everything except Scheduled (nothing sent yet). */
export const DEFAULT_ALL_INCLUDE: OutboxStatusTab[] = [
	OutboxStatusTab.IN_PROGRESS,
	OutboxStatusTab.ENDED,
	OutboxStatusTab.PAUSED,
	OutboxStatusTab.ERROR,
];

type StorageLike = Pick<Storage, "getItem" | "setItem">;

const defaultStorage = (): StorageLike | undefined =>
	typeof localStorage === "undefined" ? undefined : localStorage;

/**
 * Loads the persisted include-set, falling back to the default on missing,
 * corrupt, empty, or unknown-value data.
 */
export const loadAllInclude = (
	storage: StorageLike | undefined = defaultStorage()
): OutboxStatusTab[] => {
	try {
		const raw = storage?.getItem(ALL_INCLUDE_STORAGE_KEY);

		if (!raw) {
			return DEFAULT_ALL_INCLUDE;
		}

		const parsed = JSON.parse(raw) as unknown;

		if (!Array.isArray(parsed)) {
			return DEFAULT_ALL_INCLUDE;
		}

		const valid = parsed.filter(
			(value): value is OutboxStatusTab =>
				value !== OutboxStatusTab.ALL &&
				Object.values(OutboxStatusTab).includes(value as OutboxStatusTab)
		);

		return valid.length > 0 ? valid : DEFAULT_ALL_INCLUDE;
	} catch {
		return DEFAULT_ALL_INCLUDE;
	}
};

export const saveAllInclude = (
	include: OutboxStatusTab[],
	storage: StorageLike | undefined = defaultStorage()
): void => {
	try {
		storage?.setItem(ALL_INCLUDE_STORAGE_KEY, JSON.stringify(include));
	} catch {
		// Storage can be unavailable (private mode) — the in-memory state still applies.
	}
};

/**
 * Toggles one status inside the include-set. The set can never become empty —
 * removing the last member is a no-op (an empty All tab would show nothing).
 */
export const toggleAllInclude = (
	include: OutboxStatusTab[],
	tab: OutboxStatusTab
): OutboxStatusTab[] => {
	if (tab === OutboxStatusTab.ALL) {
		return include;
	}

	if (include.includes(tab)) {
		return include.length > 1 ? include.filter((entry) => entry !== tab) : include;
	}

	return [...include, tab];
};

/**
 * Maps the tab selection onto the API request scope: a named tab sends its single
 * bucket, the All tab sends its configured include-set.
 */
export const buildOutboxRequestScope = (
	tab: OutboxStatusTab,
	includeSet: OutboxStatusTab[]
): { outboxTabs: OutboxTab[] } => {
	if (tab === OutboxStatusTab.ALL) {
		return {
			outboxTabs: includeSet
				.filter(
					(entry): entry is Exclude<OutboxStatusTab, OutboxStatusTab.ALL> =>
						entry !== OutboxStatusTab.ALL
				)
				.map((entry) => TAB_TO_API[entry]),
		};
	}

	return { outboxTabs: [TAB_TO_API[tab]] };
};

/** Sort sentinel for the All tab's default order — resolved by the backend. */
export const LAST_MESSAGE_SENT_SORT_KEY = "lastMessageSentAt";

export interface OutboxSorting {
	sortBy: string;
	sortOrder: "ASC" | "DESC";
}

/**
 * The sort to request for a tab: an explicit user sort (URL-synced into the
 * slice) always wins; without one, the All tab defaults to last-message-sent
 * (newest first) and the other tabs keep the backend's smart default.
 */
export const resolveOutboxSort = (
	tab: OutboxStatusTab,
	urlHasExplicitSort: boolean,
	sliceSorting: OutboxSorting | undefined
): OutboxSorting | undefined => {
	if (urlHasExplicitSort) {
		return sliceSorting;
	}

	if (tab === OutboxStatusTab.ALL) {
		return { sortBy: LAST_MESSAGE_SENT_SORT_KEY, sortOrder: "DESC" };
	}

	return sliceSorting;
};
